define('presentation/settings/billing/billCycleToDate/facades/billCycleToDateFacade',['businessServices/converters/creditCardEnumerationValueConverter',
        'businessServices/converters/textConverter',
        'businessServices/creditCards/creditCardTypes',
        'businessServices/login/accountStatusEvaluator',
        'common/converters/phoneNumberFormatter',
        'common/collections/collectionSorter',
        'common/converters/passedTimeSpanFormatter',
        'common/storage/commonState',
        'common/time/date',
        'common/time/datetimeFormatter',
        'externalDependencies/kazooApi',
        'presentation/settings/billing/billCycleToDate/presentationObjects/billCycleToDatePresentationObject'],
function() {
    const BillCycleToDatePresentationObjectConstructor = require('presentation/settings/billing/billCycleToDate/presentationObjects/billCycleToDatePresentationObject');
    const _creditCardEnumerationValueConverter = require('businessServices/converters/creditCardEnumerationValueConverter');
    const CreditCardConstructor = require('businessServices/creditCards/creditCardTypes');
    let _creditCard = new CreditCardConstructor();
    let _promiseFactory = null;
    let _kazooApi = null;

    const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
    let _phoneNumberFormatter = new PhoneNumberFormatterConstructor();
    const CollectionSorterConstructor = require('common/collections/collectionSorter');
    let _collectionSorter = new CollectionSorterConstructor();
    const DateConstructor = require('common/time/date');

    const TextConverterConstructor = require('businessServices/converters/textConverter');
    let _textConverter = new TextConverterConstructor();

    const _accountStatusEvaluator = require('businessServices/login/accountStatusEvaluator');
    const _commonState = require('common/storage/commonState');
    const _i18n = require('i18next');

    const PassedTimeSpanFormatterConstructor = require('common/converters/passedTimeSpanFormatter');
    let _passedTimeSpanFormatter = new PassedTimeSpanFormatterConstructor();

    const DateTimeFormatterConstructor = require('common/time/datetimeFormatter');
    let _dateTimeFormatter = new DateTimeFormatterConstructor();

    const STARTING_BALANCE_LINE_ITEM = {
        previousBalance: {
            id: "previousbal",
            sortOrder: 1
        },
        oneTimePayment: {
            id: "onetime-payment",
            sortOrder: 2
        },
        creditNote: {
            id: "credit-note",
            sortOrder: 3
        },
        oneTimeCharge: {
            id: "onetime-charge",
            sortOrder: 3
        },
        oneTimePaymentApproved: {
            id: "approved",
            sortOrder: 4
        },
        oneTimePaymentDeclined: {
            id: "declined",
            sortOrder: 5
        },
        balanceForward: {
            id: "balanceforward",
            sortOrder: 6
        }
    };

    const _applyDiscount = (price, discountDecimal) => {
        if (!discountDecimal || parseFloat(discountDecimal) === 0) {
            return price;
        }
        const result = price - (price * discountDecimal);
        return Math.round(result * 100) / 100;
    };

    const _filterAdjustments = (adjustments, typeFilter1, typeFilter2) => {
        return adjustments.filter((adjustment) => {
            return adjustment.type === typeFilter1 || adjustment.type === typeFilter2;
        }).map((adjustment) => {
            return {
                title: adjustment.title,
                description: adjustment.description,
                amount: adjustment.amount,
                hasDiscount: false,
                retailAmount: adjustment.retailAmount,
            };
        });
    };

    const _getDefaultCreditCardInfo = (defaultCreditCardData) => {
        const defaultCreditCardInfo = _creditCardEnumerationValueConverter.getEnumerationValueByType(defaultCreditCardData.cardType);
        if (defaultCreditCardInfo === undefined) {
            let error = new Error("Invalid credit card type");
            error.defaultCreditCardData = defaultCreditCardData;
            throw error;
        } else {
            const creditCard = _creditCard.getCreditCardShortDisplay(defaultCreditCardData.cardType, defaultCreditCardData.lastFourDigits, defaultCreditCardData.expirationDate);
            return {
                lastFourDigits: defaultCreditCardData.lastFourDigits,
                displayCardNameAbbreviation: defaultCreditCardInfo.displayCardNameAbbreviation,
                shortDisplayName: creditCard.shortDisplay,
                cssClass: "icon-" + defaultCreditCardInfo.cssClass
            };
        }
    };

    const _formatDate = (dateString) => {
        if (dateString !== "") {
            let date = new DateConstructor();
            date.setFromDateString(dateString);
            return _passedTimeSpanFormatter.formatDate(date);
        } else {
            return dateString;
        }
    };

    const _formatProcessingDateDisplay = (dateString) => {
        if (dateString !== "") {
            let date = new DateConstructor();
            date.setFromDateString(dateString);
            return _dateTimeFormatter.displayDateTime(date.getISOString(), "MMM D");
        } else {
            return dateString;
        }
    };

    const _formatUsers = (userList) => {
        let users = userList.map((userItem) => {
            return {
                amount: userItem.userCost,
                isIncluded: userItem.isIncluded,
                description: userItem.emailAddress,
                title: userItem.displayName,
                hasDiscount: false,
                retailAmount: userItem.userCost,
            };
        });
        _collectionSorter.multiSort(users, ["isIncluded", "title"], [false, true]);
        return users;
    };

    const _formatScripts = (scriptList) => {
        let scripts = scriptList.map((scriptItem) => {
            return {
                amount: scriptItem.scriptCost,
                isIncluded: scriptItem.isIncluded,
                description: '',
                title: scriptItem.displayName,
                hasDiscount: false,
                retailAmount: scriptItem.scriptCost
            };
        });
        _collectionSorter.multiSort(scripts, ["isIncluded", "title"], [false, true]);
        return scripts;
    };

    const _formatPhoneNumbers = (phoneNumberList) => {
        const getPhoneNumber = (did) => {
            return _commonState.hostedNumbers().find((hostedNumber) => {
                return hostedNumber.phoneNumber() === _phoneNumberFormatter.toInternationalWithParens(did);
            });
        };

        let phoneNumbers = phoneNumberList.map((phoneNumberItem) => {
            let title = phoneNumberItem.phoneNumberName;
            let description = _phoneNumberFormatter.toInternationalWithParens(phoneNumberItem.phoneNumber);
            let commonStateHostedNumber = getPhoneNumber(phoneNumberItem.phoneNumber);

            if (commonStateHostedNumber) {
                let isTollfree = _phoneNumberFormatter.isTollFree(_phoneNumberFormatter.clean(commonStateHostedNumber.phoneNumber()));
                title = commonStateHostedNumber.hasDefaultName() ? commonStateHostedNumber.phoneNumber() : commonStateHostedNumber.name();
                description = isTollfree === true ? "Toll-Free" : commonStateHostedNumber.location();
            }
            return {
                title: title,
                phoneNumber: phoneNumberItem.phoneNumber,
                isIncluded: phoneNumberItem.isIncluded,
                description: description,
                amount: phoneNumberItem.cost,
                hasDiscount: false,
                retailAmount: phoneNumberItem.cost,
            };
        });
        _collectionSorter.multiSort(phoneNumbers, ["isIncluded", "title"], [false, true]);
        return phoneNumbers;
    };

    const _formatPlan = (planDescription, planAmount) => {
        return {
            title: "Cloud-Based Business Phone System",
            description: "",
            order: 1,
            amount: planAmount,
            hasDiscount: false,
            retailAmount: planAmount,
        };
    };

    const _formatPackage = (packageDescription, packageAmount) => {
        return {
            title: packageDescription,
            description: "",
            order: 2,
            amount: packageAmount,
            hasDiscount: false,
            retailAmount: packageAmount,
        };
    };

    const _formatRecurringCharge = (description, amount, retailAmount) => {
        const sortOrder = (description) => {
            switch (description) {
                case "Base Fee":
                case "Platform Fee":
                    return 1;
                case "User Service Fee":
                    return 2;
                case "Phone Number Service Fee":
                    return 3;
                default:
                    return 4;
            }
        };

        retailAmount = retailAmount || amount;

        return {
            title: description,
            description: "",
            order: sortOrder(description),
            amount: amount,
            retailAmount: retailAmount,
            hasDiscount: parseFloat(retailAmount) !== parseFloat(amount)
        };
    };

    const _formatPackageUsage = (includedMinutes, usedMinutes, packageUsageAmount) => {
        const options = {
            includedMinutes,
            usedMinutes
        };
        return [{
            title: _i18n.t(`billCycleToDate:packageUsage:packageMinutes:title`, options),
            description: _i18n.t(`billCycleToDate:packageUsage:packageMinutes:description`, options),
            order: 1,
            amount: packageUsageAmount || 0,
            hasDiscount: false,
            retailAmount: packageUsageAmount || 0,
        }];
    };

    const _formatReceptionistPackageUsage = (receptionistPackage) => {
        const options = {
            includedMinutes: receptionistPackage.includedMinutes,
            usedMinutes: receptionistPackage.totalMinutes
        };
        return [{
            title: _i18n.t(`billCycleToDate:receptionistPackageUsage:packageMinutes:title`, options),
            description: _i18n.t(`billCycleToDate:receptionistPackageUsage:packageMinutes:description`, options),
            order: 1,
            amount: receptionistPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: receptionistPackage.overAmount || 0,
        }];
    };

    const _formatTelephonyPackageUsage = (telephonyPackage) => {
        const options = {
            includedMinutes: telephonyPackage.includedMinutes > 999999 ? 'Unlimited' : telephonyPackage.includedMinutes,
            usedMinutes: telephonyPackage.totalMinutes
        };
        return [{
            title: _i18n.t(`billCycleToDate:telephonyPackageUsage:packageMinutes:title`, options),
            description: _i18n.t(`billCycleToDate:telephonyPackageUsage:packageMinutes:description`, options),
            order: 1,
            amount: telephonyPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: telephonyPackage.overAmount || 0,
        }];
    };

    const _formatOutboundCallingPackageUsage = (outboundCallingPackage) => {
        const options = {
            included: outboundCallingPackage.includedContacts || 40,
            used: outboundCallingPackage.totalContacts || 0
        };
        return [{
            title: _i18n.t(`billCycleToDate:outboundCallingPackageUsage:packageUsage:title`, options),
            description: _i18n.t(`billCycleToDate:outboundCallingPackageUsage:packageUsage:description`, options),
            order: 1,
            amount: outboundCallingPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: outboundCallingPackage.overAmount || 0,
        }];
    };

    const _formatWebChatPackageUsage = (webChatPackage) => {
        const options = {
            included: webChatPackage.includedChats || 0,
            used: webChatPackage.totalChats || 0
        };
        return [{
            title: _i18n.t(`billCycleToDate:webChatPackageUsage:packageUsage:title`, options),
            description: _i18n.t(`billCycleToDate:webChatPackageUsage:packageUsage:description`, options),
            order: 1,
            amount: webChatPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: webChatPackage.overAmount || 0,
        }];
    };

    const _formatAdditionalLiveCallAnsweringMinutes = (receptionistPackage) => {
        const options = {
            includedMinutes: receptionistPackage.includedMinutes,
            usedMinutes: receptionistPackage.totalMinutes
        };
        return [{
            title: _i18n.t(`billCycleToDate:additionalLiveCallAnsweringMinutes:packageMinutes:title`, options),
            description: _i18n.t(`billCycleToDate:additionalLiveCallAnsweringMinutes:packageMinutes:description`, options),
            order: 1,
            amount: receptionistPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: receptionistPackage.overAmount || 0,
        }];
    };

    const _formatAdditionalAutomatedMinutes = (telephonyPackage) => {
        const options = {
            includedMinutes: telephonyPackage.includedMinutes > 999999 ? 'Unlimited' : telephonyPackage.includedMinutes,
            usedMinutes: telephonyPackage.totalMinutes
        };
        return [{
            title: _i18n.t(`billCycleToDate:additionalAutomatedMinutes:packageMinutes:title`, options),
            description: _i18n.t(`billCycleToDate:additionalAutomatedMinutes:packageMinutes:description`, options),
            order: 1,
            amount: telephonyPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: telephonyPackage.overAmount || 0,
        }];
    };

    const _formatAdditionalOutreaches = (outboundCallingPackage) => {
        const options = {
            included: outboundCallingPackage.includedOutreaches,
            used: outboundCallingPackage.totalOutreaches
        };
        return [{
            title: _i18n.t(`billCycleToDate:additionalOutreaches:packageUsage:title`, options),
            description: _i18n.t(`billCycleToDate:additionalOutreaches:packageUsage:description`, options),
            order: 1,
            amount: outboundCallingPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: outboundCallingPackage.overAmount || 0,
        }];
    };

    const _formatAdditionalWebChats = (webChatPackage) => {
        const options = {
            included: webChatPackage.includedChats || 0,
            used: webChatPackage.totalChats || 0
        };
        return [{
            title: _i18n.t(`billCycleToDate:additionalWebChats:packageUsage:title`, options),
            description: _i18n.t(`billCycleToDate:additionalWebChats:packageUsage:description`, options),
            order: 1,
            amount: webChatPackage.overAmount || 0,
            hasDiscount: false,
            retailAmount: webChatPackage.overAmount || 0,
        }];
    };

    const _formatTaxesFeesSurcharges = (taxesFeesSurchargesList) => {
        let taxesFeesSurcharges = taxesFeesSurchargesList.map((taxFeeSurcharge) => {
            return {
                title: _textConverter.capitalizeEachWord(taxFeeSurcharge.taxDescription),
                description: "",
                order: taxFeeSurcharge.order,
                amount: taxFeeSurcharge.taxAmount,
                hasDiscount: false,
                retailAmount: taxFeeSurcharge.taxAmount,
            };
        });

        _collectionSorter.sort(taxesFeesSurcharges, "order", true);
        return taxesFeesSurcharges;
    };

    const _formatMonthlyRecurringCharges = ({monthlyRecurringCharges, adjustments}) => {
        monthlyRecurringCharges = monthlyRecurringCharges || [];
        adjustments = adjustments || [];
        let sequence = 0;
        let formattedRecurringCharges = monthlyRecurringCharges.map(({description, amount}) => {
            return _formatRecurringCharge(description, amount);
        }).filter((recurringCharge) => recurringCharge.amount > 0);

        const packageChanges = _filterAdjustments(adjustments, "package");
        let formattedPackageChanges = packageChanges.map((packageChange) => {
            return _formatRecurringCharge(packageChange.description, packageChange.amount);
        });

        let discounts = _filterAdjustments(adjustments, "discount");
        let formattedDiscounts = discounts.map((discount) => {
            return _formatRecurringCharge(discount.description, discount.amount);
        });

        _collectionSorter.multiSort(formattedRecurringCharges, ["order", "title"], [true, true]);
        _collectionSorter.multiSort(formattedPackageChanges, ["order", "title"], [true, true]);
        _collectionSorter.multiSort(formattedDiscounts, ["order", "title"], [true, true]);

        return [...formattedRecurringCharges, ...formattedPackageChanges, ...formattedDiscounts];
    };

    const _formatMonthlyRecurringChargesForLiveServices = (monthlyRecurringCharges, discountDecimal, accountPlan) => {
        monthlyRecurringCharges = monthlyRecurringCharges || [];
        let formattedRecurringCharges = monthlyRecurringCharges.filter((recurringCharge) => recurringCharge.amount > 0 && recurringCharge.serviceType === 'Live')
            .map(({description, retailAmount}) => {
                const amountAfterDiscount = _applyDiscount(retailAmount, discountDecimal);
                return _formatRecurringCharge(description, amountAfterDiscount, retailAmount);
        });

        _collectionSorter.multiSort(formattedRecurringCharges, ["order", "title"], [true, true]);
        return [...formattedRecurringCharges];
    };

    const _formatMonthlyRecurringChargesForLiveServicesInOnboarding = (accountPlan, discountDecimal) => {
        let monthlyRecurringCharges = [];
        if (accountPlan && accountPlan.liveReceptionistPackage)
        {
            monthlyRecurringCharges.push({
                description: "Live Call Answering, " + accountPlan.liveReceptionistPackage.name,
                retailAmount: accountPlan.liveReceptionistPackage.pricePerMonth || 0
            });
        }
        if (accountPlan && accountPlan.outboundCallingPackage)
        {
            monthlyRecurringCharges.push({
                description: "Live Outbound Calling, " + accountPlan.outboundCallingPackage.name,
                retailAmount: accountPlan.outboundCallingPackage.pricePerMonth || 0
            });
        }
        if (accountPlan && accountPlan.webChatPackage)
        {
            monthlyRecurringCharges.push({
                description: "Live Web Chats, " + accountPlan.webChatPackage.name,
                retailAmount: accountPlan.webChatPackage.pricePerMonth || 0
            });
        }

        let formattedRecurringCharges = monthlyRecurringCharges.map(({description, retailAmount}) => {
                const amountAfterDiscount = _applyDiscount(retailAmount, discountDecimal);
                return _formatRecurringCharge(description, amountAfterDiscount, retailAmount);
            });

        _collectionSorter.multiSort(formattedRecurringCharges, ["order", "title"], [true, true]);
        return [...formattedRecurringCharges];
    };

    const _formatMonthlyRecurringChargesForAutomatedServices = (monthlyRecurringCharges, discountDecimal, phoneNumbers) => {
        monthlyRecurringCharges = monthlyRecurringCharges || [];
        let sequence = 0;
        let formattedRecurringCharges = monthlyRecurringCharges.filter((recurringCharge) => recurringCharge.serviceType === "Automated")
            .map(({description, retailAmount}) => {
                const amountAfterDiscount = _applyDiscount(retailAmount, discountDecimal);
                return _formatRecurringCharge(description, amountAfterDiscount, retailAmount);
        });
        let formattedPhoneNumbers = _formatPhoneNumbers(phoneNumbers);

        _collectionSorter.multiSort(formattedRecurringCharges, ["order", "title"], [true, true]);

        return [...formattedRecurringCharges, ...formattedPhoneNumbers];
    };

    const _formatMonthlyRecurringChargesForAutomatedServicesInOnboarding = (accountPlan, discountDecimal, phoneNumbers) => {
        let monthlyRecurringCharges = [];
        const hasLiveReceptionistPackage= accountPlan.liveReceptionistPackage !== undefined;
        const hasOutboundCallingPackage = accountPlan.liveReceptionistPackage !== undefined;
        const hasWebChatPackage = accountPlan.webChatPackage !== undefined;

        const applyAutomatedServicesCreditLineItem= hasLiveReceptionistPackage === true ||
            hasOutboundCallingPackage === true ||
            hasWebChatPackage === true;

        if (accountPlan && accountPlan.plan && accountPlan.telephonyPackage)
        {
            monthlyRecurringCharges.push({
                description: accountPlan.telephonyPackage.name,
                retailAmount: accountPlan.telephonyPackage.pricePerMonth || 0
            });

            if (applyAutomatedServicesCreditLineItem === true) {

                monthlyRecurringCharges.push({
                    description: accountPlan.telephonyPackage.name + " Credit",
                    retailAmount: (accountPlan.telephonyPackage.pricePerMonth || 0) * -1
                });
            }
        }

        let formattedRecurringCharges = monthlyRecurringCharges.map(({description, retailAmount}) => {
                const amountAfterDiscount = _applyDiscount(retailAmount, discountDecimal);
                return _formatRecurringCharge(description, amountAfterDiscount, retailAmount);
            });
        let formattedPhoneNumbers = _formatPhoneNumbers(phoneNumbers);

        _collectionSorter.multiSort(formattedRecurringCharges, ["order", "title"], [true, true]);

        return [...formattedRecurringCharges, ...formattedPhoneNumbers];
    };

    const _formatPackageChangeAdjustments = (adjustments, discountDecimal) => {
        const packageChanges = _filterAdjustments(adjustments, "package") || [];
        let formattedPackageChanges = packageChanges.map((packageChange) => {
            const discountedAmount = _applyDiscount(packageChange.amount, discountDecimal);
            return _formatRecurringCharge(packageChange.description, discountedAmount, packageChange.amount);
        });
        _collectionSorter.multiSort(formattedPackageChanges, ["order", "title"], [true, true]);
        return [...formattedPackageChanges];
    };

    const _formatAdjustments = (adjustments, discount) => {
        const packageChanges = _filterAdjustments(adjustments, "package") || [];
        let formattedPackageChanges = packageChanges.map((packageChange) => {
            return _formatRecurringCharge(packageChange.description, packageChange.amount);
        });
        _collectionSorter.multiSort(formattedPackageChanges, ["order", "title"], [true, true]);
        return [...formattedPackageChanges];
    };

    const _formatStartingBalance = (currentStatement, isFreeTrialPendingOnboarding) => {
        const sortOrder = (type) => {
            switch (type) {
                case STARTING_BALANCE_LINE_ITEM.previousBalance.id:
                    return STARTING_BALANCE_LINE_ITEM.previousBalance.sortOrder;
                case STARTING_BALANCE_LINE_ITEM.oneTimePayment.id:
                    return STARTING_BALANCE_LINE_ITEM.oneTimePayment.sortOrder;
                case STARTING_BALANCE_LINE_ITEM.oneTimeCharge.id:
                    return STARTING_BALANCE_LINE_ITEM.oneTimeCharge.sortOrder;
                case STARTING_BALANCE_LINE_ITEM.oneTimePaymentApproved.id:
                    return STARTING_BALANCE_LINE_ITEM.oneTimePaymentApproved.sortOrder;
                case STARTING_BALANCE_LINE_ITEM.oneTimePaymentDeclined.id:
                    return STARTING_BALANCE_LINE_ITEM.oneTimePaymentDeclined.sortOrder;
                case STARTING_BALANCE_LINE_ITEM.balanceForward.id:
                    return STARTING_BALANCE_LINE_ITEM.balanceForward.sortOrder;
                default:
                    let unknownTypeError = new Error("item has an unknown type");
                    unknownTypeError.type = type;
                    throw unknownTypeError;
            }
        };

        // During the free trial these rows are all titled "Free Trial started on …", which isn't true
        // until onboarding finishes.
        const previousBalanceAndPaymentAttempts = isFreeTrialPendingOnboarding ? [] : currentStatement.previousBalanceAndPaymentAttempts;
        let payments = previousBalanceAndPaymentAttempts
            .map((startingBalanceItem) => {
                let title;
                if (currentStatement.isInFreeTrial === true) {
                    const options = {
                        freeTrialStartDate: _formatDate(currentStatement.freeTrialStartDate)
                    };
                    title = _i18n.t(`billCycleToDate:freeTrialStartedOn`, options);
                } else if (currentStatement.hasCurrentBalance && startingBalanceItem.type === STARTING_BALANCE_LINE_ITEM.previousBalance) {
                    const options = {
                        cycleStartDate: _formatDate(currentStatement.currentBalanceCycleStartDate),
                        cycleEndDate: _formatDate(currentStatement.currentBalanceCycleEndDate)
                    };
                    title = _i18n.t(`billCycleToDate:invoiceFor`, options);
                } else {
                    title = startingBalanceItem.title + " " + startingBalanceItem.description;
                }
                return {
                    title: title,
                    amount: startingBalanceItem.amount,
                    order: sortOrder(startingBalanceItem.type),
                    hasDiscount: false,
                    retailAmount: startingBalanceItem.amount,
                };
            });

        let credits = _filterAdjustments(currentStatement.adjustments, STARTING_BALANCE_LINE_ITEM.creditNote.id, STARTING_BALANCE_LINE_ITEM.oneTimeCharge.id)
            .map((credit) => {
                return {
                    title: credit.description,
                    description: "",
                    amount: credit.amount,
                    order: sortOrder(STARTING_BALANCE_LINE_ITEM.oneTimeCharge.id),
                    hasDiscount: false,
                    retailAmount: credit.amount,
                };
            });

        let remainingBalance = {
            title: _i18n.t(`billCycleToDate:remainingBalance`),
            description: "",
            amount: currentStatement.currentBalance,
            order: sortOrder(STARTING_BALANCE_LINE_ITEM.balanceForward.id),
            includeInTotal: false,
            hasDiscount: false,
            retailAmount: currentStatement.currentBalance
        };

        let startingItems = payments.concat(credits).concat(remainingBalance);
        _collectionSorter.multiSort(startingItems, ["order", "title"]);
        return startingItems;
    };

    const _getCurrentStatement = () => {
        return _promiseFactory.defer((deferredObject) => {
            let billCycleToDatePresentationObject = new BillCycleToDatePresentationObjectConstructor();
            _kazooApi.callAccount('/lobby_bill_cycle_to_date','GET')
                .fail(deferredObject.reject)
                .done((result) => {
                    let currentStatement = result.data.data;
                    billCycleToDatePresentationObject.estimatedCharges = currentStatement.estimatedCharges;
                    billCycleToDatePresentationObject.estimatedChargesProcessingDateDisplay = _formatProcessingDateDisplay(currentStatement.cycleChargeDate);

                    billCycleToDatePresentationObject.doNotAutoCharge = currentStatement.doNotAutoCharge;

                    billCycleToDatePresentationObject.hasPastDueBalance = currentStatement.hasPastDueBalance;
                    billCycleToDatePresentationObject.pastDueBalance = currentStatement.pastDueBalance;
                    billCycleToDatePresentationObject.pastDueBalanceCycleStartDate = _formatDate(currentStatement.pastDueBalanceCycleStartDate);
                    billCycleToDatePresentationObject.pastDueBalanceCycleEndDate = _formatDate(currentStatement.pastDueBalanceCycleEndDate);
                    billCycleToDatePresentationObject.pastDueBalanceAutoCloseDate = _formatDate(currentStatement.pastDueBalanceAutoCloseDate);

                    billCycleToDatePresentationObject.hasCurrentBalance = currentStatement.hasCurrentBalance;
                    billCycleToDatePresentationObject.currentBalance = currentStatement.currentBalance;
                    billCycleToDatePresentationObject.currentBalanceCycleStartDate = _formatDate(currentStatement.currentBalanceCycleStartDate);
                    billCycleToDatePresentationObject.currentBalanceCycleEndDate = _formatDate(currentStatement.currentBalanceCycleEndDate);

                    if (currentStatement.currentBalanceChargeDate !== "") {
                        billCycleToDatePresentationObject.currentBalanceChargeDate = _formatDate(currentStatement.currentBalanceChargeDate);
                        billCycleToDatePresentationObject.currentBalanceProcessingDateDisplay = _formatProcessingDateDisplay(currentStatement.currentBalanceChargeDate);

                    } else {
                        billCycleToDatePresentationObject.currentBalanceChargeDate = _formatDate(currentStatement.cycleChargeDate);
                        billCycleToDatePresentationObject.currentBalanceProcessingDateDisplay = _formatProcessingDateDisplay(currentStatement.cycleChargeDate);
                    }

                    if (currentStatement.defaultPaymentMethod)
                    {
                        billCycleToDatePresentationObject.defaultPaymentMethod = _getDefaultCreditCardInfo(currentStatement.defaultPaymentMethod);
                    }

                    billCycleToDatePresentationObject.currentChargeDate = _formatDate(currentStatement.cycleChargeDate);

                    if (currentStatement.planAmount > 0) {
                        billCycleToDatePresentationObject.displayPlan = true;
                    }

                    billCycleToDatePresentationObject.isLegacyBillFormat = currentStatement.isLegacyBillFormat;

                    billCycleToDatePresentationObject.plan = [_formatPlan(currentStatement.planDescription, currentStatement.planAmount)];

                    const liveReceptionistPackage = currentStatement.packages.find((p) => p.packageType === "LiveReceptionist");
                    if (liveReceptionistPackage) {
                        billCycleToDatePresentationObject.hasReceptionistPackage = true;
                        billCycleToDatePresentationObject.receptionistPackageUsage = _formatReceptionistPackageUsage(liveReceptionistPackage);
                        billCycleToDatePresentationObject.receptionistPackageMinutesUsed = liveReceptionistPackage.totalMinutes;
                    }

                    const telephonyPackage = currentStatement.packages.find((p) => p.packageType === "Telephony");
                    if (telephonyPackage) {
                        billCycleToDatePresentationObject.hasTelephonyPackage = true;
                        billCycleToDatePresentationObject.telephonyPackageUsage = _formatTelephonyPackageUsage(telephonyPackage);
                        billCycleToDatePresentationObject.telephonyPackageMinutesUsed = telephonyPackage.totalMinutes;
                    }

                    const outboundCallingPackage = currentStatement.packages.find((p) => p.packageType === "OutboundCalling");
                    if (outboundCallingPackage) {
                        billCycleToDatePresentationObject.hasOutboundCallingPackage = true;
                        billCycleToDatePresentationObject.outboundCallingPackageUsage = _formatOutboundCallingPackageUsage(outboundCallingPackage);
                        billCycleToDatePresentationObject.outboundCallingContactsUsed = outboundCallingPackage.totalContacts || 0;
                    }

                    const webChatPackage = currentStatement.packages.find((p) => p.packageType === "WebChat");
                    if (webChatPackage) {
                        billCycleToDatePresentationObject.hasWebChatPackage = true;
                        billCycleToDatePresentationObject.webChatPackageUsage = _formatWebChatPackageUsage(webChatPackage);
                        billCycleToDatePresentationObject.webChatsUsed = webChatPackage.totalChats || 0;
                    }

                    billCycleToDatePresentationObject.users = _formatUsers(currentStatement.users);
                    billCycleToDatePresentationObject.scripts = _formatScripts(currentStatement.scripts);
                    billCycleToDatePresentationObject.phoneNumbers = _formatPhoneNumbers(currentStatement.phoneNumbers);

                    billCycleToDatePresentationObject.taxesFeesSurcharges = _formatTaxesFeesSurcharges(currentStatement.taxesFeesSurcharges);

                    const isFreeTrialPendingOnboarding = _accountStatusEvaluator.isWelcomeInfoCapture();
                    const isAccountInOnboarding = currentStatement.isAccountInOnboarding;

                    billCycleToDatePresentationObject.startingBalance = _formatStartingBalance(currentStatement, isFreeTrialPendingOnboarding);
                    billCycleToDatePresentationObject.monthlyRecurringCharges = _formatMonthlyRecurringCharges(currentStatement);

                    billCycleToDatePresentationObject.isAccountInOnboarding = currentStatement.isAccountInOnboarding;
                    billCycleToDatePresentationObject.isInFreeTrial = currentStatement.isInFreeTrial;
                    billCycleToDatePresentationObject.isFreeTrialPendingOnboarding = isFreeTrialPendingOnboarding;
                    billCycleToDatePresentationObject.freeTrialStartDate = _formatDate(currentStatement.freeTrialStartDate);
                    billCycleToDatePresentationObject.freeTrialEndDate = _formatDate(currentStatement.freeTrialEndDate);
                    billCycleToDatePresentationObject.numberOfDaysRemainingInFreeTrial = currentStatement.numberOfDaysRemainingInFreeTrial;
                    billCycleToDatePresentationObject.cycleStartDate = _formatDate(currentStatement.cycleStartDate);
                    billCycleToDatePresentationObject.cycleEndDate = _formatDate(currentStatement.cycleEndDate);
                    billCycleToDatePresentationObject.nextCycleStartDate = _formatDate(currentStatement.nextCycleStartDate);
                    billCycleToDatePresentationObject.nextCycleEndDate = _formatDate(currentStatement.nextCycleEndDate);

                    if (currentStatement.isLegacyBillFormat === false)
                    {
                        const discountDecimal = currentStatement.monthlyRecurringDiscountPercent || 0;
                        const monthlyRecurringCharges = currentStatement.monthlyRecurringCharges || [];
                        const phoneNumbers = currentStatement.phoneNumbers || [];
                        const accountPlan = currentStatement.accountPlan;

                        // Clear out Legacy Monthly Recurring Charges
                        billCycleToDatePresentationObject.monthlyRecurringCharges = [];
                        billCycleToDatePresentationObject.phoneNumbers = [];

                        // billCycleToDatePresentationObject.monthlyRecurringChargesForAutomatedServices = _formatMonthlyRecurringChargesForAutomatedServices(monthlyRecurringCharges, discountDecimal, phoneNumbers);
                        billCycleToDatePresentationObject.monthlyRecurringChargesForAutomatedServices = isAccountInOnboarding === true ?
                            _formatMonthlyRecurringChargesForAutomatedServicesInOnboarding(accountPlan, discountDecimal, phoneNumbers) :
                            _formatMonthlyRecurringChargesForAutomatedServices(monthlyRecurringCharges, discountDecimal, phoneNumbers);

                        // billCycleToDatePresentationObject.monthlyRecurringChargesForLiveServices = _formatMonthlyRecurringChargesForLiveServices(monthlyRecurringCharges, discountDecimal);
                        billCycleToDatePresentationObject.monthlyRecurringChargesForLiveServices = isAccountInOnboarding === true ?
                            _formatMonthlyRecurringChargesForLiveServicesInOnboarding(accountPlan, discountDecimal, phoneNumbers) :
                            _formatMonthlyRecurringChargesForLiveServices(monthlyRecurringCharges, discountDecimal, phoneNumbers);

                        billCycleToDatePresentationObject.adjustments = _formatPackageChangeAdjustments(currentStatement.adjustments, discountDecimal);

                        if (telephonyPackage)
                        {
                            billCycleToDatePresentationObject.additionalAutomatedMinutes = _formatAdditionalAutomatedMinutes(telephonyPackage);
                            billCycleToDatePresentationObject.telephonyPackageMinutesUsed = telephonyPackage.overMinutes || 0;
                            billCycleToDatePresentationObject.telephonyPackageUsage = [];
                        }

                        if (liveReceptionistPackage)
                        {
                            billCycleToDatePresentationObject.additionalLiveCallAnsweringMinutes = _formatAdditionalLiveCallAnsweringMinutes(liveReceptionistPackage);
                            billCycleToDatePresentationObject.receptionistPackageMinutesUsed = liveReceptionistPackage.overMinutes || 0;
                            billCycleToDatePresentationObject.receptionistPackageUsage = [];
                        }

                        if (outboundCallingPackage)
                        {
                            billCycleToDatePresentationObject.additionalOutreaches = _formatAdditionalOutreaches(outboundCallingPackage);
                            billCycleToDatePresentationObject.outboundCallingContactsUsed = outboundCallingPackage.overOutreaches || 0;
                            billCycleToDatePresentationObject.outboundCallingPackageUsage = [];
                        }

                        if (webChatPackage)
                        {
                            billCycleToDatePresentationObject.additionalWebChats = _formatAdditionalWebChats(webChatPackage);
                            billCycleToDatePresentationObject.webChatsUsed = webChatPackage.overChats || 0;
                            billCycleToDatePresentationObject.webChatPackageUsage = [];
                        }

                        // Clear out Legacy taxes fees
                        billCycleToDatePresentationObject.taxesFeesSurcharges = [];
                        const currentTaxesFeesAndSurcharges = currentStatement.taxesFeesSurcharges || [];
                        const federalTaxes = currentTaxesFeesAndSurcharges.filter(tax => tax.taxAuthority === "federal");
                        const stateTaxes = currentTaxesFeesAndSurcharges.filter(tax => tax.taxAuthority === "state");
                        const localTaxes = currentTaxesFeesAndSurcharges.filter(tax => tax.taxAuthority === "county" || tax.taxAuthority === "city" || tax.taxAuthority === "local");

                        billCycleToDatePresentationObject.currentFederalTaxes = _formatTaxesFeesSurcharges(federalTaxes);
                        billCycleToDatePresentationObject.currentStateTaxes = _formatTaxesFeesSurcharges(stateTaxes);
                        billCycleToDatePresentationObject.currentLocalTaxes = _formatTaxesFeesSurcharges(localTaxes);
                    }

                    deferredObject.resolve(billCycleToDatePresentationObject);
                });
        });
    };

    const _init = (promiseFactory) => {
        _promiseFactory = promiseFactory;

        const KazooConstructor = require('externalDependencies/kazooApi');
        _kazooApi = new KazooConstructor();
    };

    return function() {
        const self = this;

        self.init = _init;
        self.getCurrentStatement = _getCurrentStatement;
    };
});

